import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { createSelector } from "reselect";
import { orderBy as sort } from "lodash";

import { SetTicketsAction } from "../interface/redux-types/SetTicketsAction";
import { RootState } from "..";
import { TicketState } from "../interface/redux-types/TicketState";
import { SetTicketAction } from "../interface/redux-types/SetTicketAction";
import { SetTicketInTicketsAction } from "../interface/redux-types/SetTicketInTicketsAction";
import { isDateEqualTo } from "../utils/date-helper";
import { latestReceivedFirst } from "../utils/ticket-helpers";
import { Ticket } from "../interface/api/Ticket";

const initialState: TicketState = {
  tickets: [],
  ticket: null,
  showActive: true,
  showReceived: false,
  sortBy: "ticketNumber",
  receivedTicketCountForToday: 0,
  uniqueJobSitesCountForToday: 0,
  uniqueContractorsCountForToday: 0,
};

const slice = createSlice({
  name: "ticket",
  initialState,
  reducers: {
    setTickets: (state, action: PayloadAction<SetTicketsAction>) => {
      state.tickets = action.payload.tickets;
    },

    setTicketInTickets: (
      state,
      action: PayloadAction<SetTicketInTicketsAction>
    ) => {
      const { ticket } = action.payload;

      const index = state.tickets.findIndex((t) => t.id === ticket.id);
      state.tickets[index] = ticket;
    },
    setTicket: (state, action: PayloadAction<SetTicketAction>) => {
      state.ticket = action.payload.ticket;
    },
    setFilterToggleShowActive: (state) => {
      state.showActive = !state.showActive;
      state.showReceived = false;
    },
    setFilterToggleShowReceived: (state) => {
      state.showReceived = !state.showReceived;
      state.showActive = false;
    },
    updateBulkTickets: (state, action: PayloadAction<SetTicketsAction>) => {
      const updatedTickets = action.payload.tickets;
      for (let updatedTicket of updatedTickets) {
        let index = state.tickets.findIndex((t) => t.id === updatedTicket.id);
        state.tickets[index] = updatedTicket;
      }
    },
    updateSortBy: (
      state,
      action: PayloadAction<"ticketNumber" | "contractorCompanyName" | "shippingCompany">
    ) => {
      state.sortBy = action.payload;
    },
    setReceivedTicketCountForToday: (state, action: PayloadAction<number>) => {
      state.receivedTicketCountForToday = action.payload;
    },
    setUniqueJobSitesCountForToday: (state, action: PayloadAction<number>) => {
      state.uniqueJobSitesCountForToday = action.payload;
    },
    setUniqueContractorsCountForToday: (
      state,
      action: PayloadAction<number>
    ) => {
      console.log("Setting unique contractors count:", action.payload);
      state.uniqueContractorsCountForToday = action.payload;
    },
  },
});

export const {
  setTickets,
  setTicketInTickets,
  setTicket,
  updateBulkTickets,
  setFilterToggleShowActive,
  setFilterToggleShowReceived,
  updateSortBy,
  setReceivedTicketCountForToday,
  setUniqueContractorsCountForToday,
  setUniqueJobSitesCountForToday,
} = slice.actions;

// What the Received toggle lists: received today, or loaded today and since received.
const isReceivedToday = (t: Ticket) =>
  t.isReceived &&
  (isDateEqualTo(t.timeReceived, new Date()) ||
    isDateEqualTo(t.timeLoaded, new Date()));

export const getTickets = createSelector(
  (state: RootState) => state.ticketStore.tickets,
  (state: RootState) => state.ticketStore.showActive,
  (state: RootState) => state.ticketStore.showReceived,
  (tickets, showActive, showReceived) => {
    if (showActive) {
      tickets = tickets.filter(
        (t) => isDateEqualTo(t.timeLoaded, new Date()) && t.isReceived === false
      );
    }

    if (showReceived) {
      return latestReceivedFirst(tickets.filter(isReceivedToday), (t) => t.timeReceived);
    }

    tickets = sort(tickets, ["ticketNumber"], ["desc"]);
    return tickets;
  }
);

export const getReportTickets = createSelector(
  (state: RootState) => state.ticketStore.tickets,
  (state: RootState) => state.ticketStore.sortBy,

  (tickets, sortBy) => {
    if (sortBy === "ticketNumber") {
      tickets = sort(tickets, [sortBy], ["desc"]);
    }
    if (sortBy === "contractorCompanyName") {
      tickets = sort(tickets, [sortBy], ["asc"]);
    }
    return tickets;
  }
);

export const getTicket = createSelector(
  (state: RootState) => state.ticketStore.ticket,
  (ticket) => {
    return ticket;
  }
);

export const getShowActive = createSelector(
  (state: RootState) => state.ticketStore.showActive,
  (showActive) => {
    return showActive;
  }
);

export const getShowReceived = createSelector(
  (state: RootState) => state.ticketStore.showReceived,
  (showReceived) => {
    return showReceived;
  }
);

export const getReceivedTodayCount = createSelector(
  (state: RootState) => state.ticketStore.tickets,
  (tickets) => tickets.filter(isReceivedToday).length
);

export const getTodaysTickets = createSelector(
  (state: RootState) => state.ticketStore.tickets,
  (tickets) => {
    tickets = tickets.filter((t) => isDateEqualTo(t.timeLoaded, new Date()));
    return tickets;
  }
);

export const getTicketCount = createSelector(
  (state: RootState) => state.ticketStore.tickets,
  (tickets) => {
    return tickets.length;
  }
);

export const getSortBy = createSelector(
  (state: RootState) => state.ticketStore.sortBy,
  (sortBy) => {
    return sortBy;
  }
);

export const getReceivedTicketCountForToday = createSelector(
  (state: RootState) => state.ticketStore.receivedTicketCountForToday,
  (receivedTicketCountForToday) => {
    return receivedTicketCountForToday;
  }
);

export const getUniqueJobSitesCountForToady = createSelector(
  (state: RootState) => state.ticketStore.uniqueJobSitesCountForToday,
  (uniqueJobSitesCountForToday) => {
    return uniqueJobSitesCountForToday;
  }
);

export const getUniqueContractorsCountForToday = createSelector(
  (state: RootState) => state.ticketStore.uniqueContractorsCountForToday,
  (uniqueContractorsCountForToday) => {
    return uniqueContractorsCountForToday;
  }
);

export const reducer = slice.reducer;
